import type { NatcaNavSection, NatcaTab } from '@natca-itc/ui-shell'
import type { Role } from './types'

interface NavDef {
  id: string
  label: string
  icon: string
  to: string
  roles?: Role[]
}

const SECTIONS: { title: string; items: NavDef[] }[] = [
  {
    title: 'Overview',
    items: [
      { id: 'home', label: 'Home', icon: 'mdi-home-outline', to: '/' },
      // Everyone registers here, members, staff and former presidents alike (R18).
      { id: 'registration', label: 'Register', icon: 'mdi-ticket-confirmation-outline', to: '/registration' },
    ],
  },
  {
    title: 'Before the convention',
    items: [
      { id: 'delegation', label: 'Local Delegation', icon: 'mdi-account-group-outline', to: '/delegation', roles: ['facrep', 'credentials'] },
      { id: 'declare', label: 'Declare delegates', icon: 'mdi-account-multiple-plus-outline', to: '/declare', roles: ['facrep'] },
      { id: 'declarations', label: 'Declarations', icon: 'mdi-clipboard-list-outline', to: '/credentials/declarations', roles: ['credentials'] },
      { id: 'snapshot', label: 'T-30 snapshot', icon: 'mdi-camera-timer', to: '/snapshot', roles: ['credentials'] },
      { id: 'report', label: 'Credentials report', icon: 'mdi-file-document-outline', to: '/credentials/report', roles: ['credentials'] },
    ],
  },
  {
    title: 'On the floor',
    items: [
      { id: 'desk', label: 'Credentials desk', icon: 'mdi-badge-account-horizontal-outline', to: '/desk', roles: ['desk', 'credentials'] },
      { id: 'roll-call', label: 'Roll call', icon: 'mdi-format-list-numbered', to: '/roll-call', roles: ['rollcall', 'credentials'] },
      { id: 'mic-stand', label: 'Mic stand', icon: 'mdi-qrcode-scan', to: '/mic/stand', roles: ['saa', 'spotter'] },
      { id: 'mic-queue', label: 'Mic queue', icon: 'mdi-microphone-outline', to: '/mic/queue', roles: ['spotter'] },
    ],
  },
  {
    title: 'Admin',
    items: [
      { id: 'admin-convention', label: 'Convention settings', icon: 'mdi-cog-outline', to: '/admin/convention', roles: ['admin'] },
      { id: 'admin-staff', label: 'Staff and roles', icon: 'mdi-account-key-outline', to: '/admin/staff', roles: ['admin'] },
      { id: 'admin-registration', label: 'Registration', icon: 'mdi-ticket-account', to: '/admin/registration', roles: ['admin'] },
      { id: 'admin-agenda', label: 'Amendments', icon: 'mdi-format-list-text', to: '/admin/agenda', roles: ['admin'] },
      { id: 'admin-displays', label: 'Displays', icon: 'mdi-monitor-dashboard', to: '/admin/displays', roles: ['admin', 'spotter'] },
      { id: 'admin-audit', label: 'Audit log', icon: 'mdi-history', to: '/admin/audit', roles: ['admin'] },
    ],
  },
]

export function navFor(hasRole: (...roles: Role[]) => boolean): { sections: NatcaNavSection[]; tabs: NatcaTab[] } {
  const visible = (i: NavDef) => !i.roles || hasRole(...i.roles)
  const sections = SECTIONS
    .map((s) => ({ title: s.title, items: s.items.filter(visible).map(({ id, label, icon, to }) => ({ id, label, icon, to })) }))
    .filter((s) => s.items.length > 0)
  const tabs: NatcaTab[] = [
    { id: 'home', label: 'Home', icon: 'mdi-home-outline', to: '/' },
    { id: 'registration', label: 'Register', icon: 'mdi-ticket-confirmation-outline', to: '/registration' },
  ]
  // FacReps get their one job in the header too (BUILD-PLAN 0d).
  if (hasRole('facrep')) tabs.push({ id: 'delegation', label: 'Local Delegation', icon: 'mdi-account-group-outline', to: '/delegation' })
  return { sections, tabs }
}

/**
 * Where a person lands after signing in. A member whose only role is FacRep
 * lands on their Local's dashboard, Local Delegation (0d); a Sergeant-at-Arms on
 * the mic stand page; the spotter on the mic queue (R28). Everyone else lands home.
 */
export function landingFor(roles: readonly Role[]): string {
  if (roles.length === 1 && roles[0] === 'facrep') return '/delegation'
  // Floor staff open the app (often from a phone's home screen) straight onto their one job.
  const only = (...allowed: Role[]) => roles.length > 0 && roles.every((r) => allowed.includes(r))
  if (only('spotter', 'saa') && roles.includes('spotter')) return '/mic/queue'
  if (only('saa')) return '/mic/stand'
  return '/'
}
